import { NodeStepType } from '@ui/__generated__/graphql'
import { create } from 'zustand'

export type CodeStepConfig = {
  code: string
}

export type DropStepConfig = {
  dropEntireEvent: boolean
  dropFields: string[]
}

export type RegexStepConfig = {
  field: string
  regex: string
  keepMatch: boolean
}

export type SampleStepConfig = {
  samplePercentage: number // 0-100
}

export type FilterStepConfig = object

export type Config = {
  [NodeStepType.Code]: CodeStepConfig
  [NodeStepType.Drop]: DropStepConfig
  [NodeStepType.Regex]: RegexStepConfig
  [NodeStepType.Sample]: SampleStepConfig
  [NodeStepType.Filter]: FilterStepConfig
}[NodeStepType]

const defaultFilterStepConfig: FilterStepConfig = {}

const defaultCodeStepConfig: CodeStepConfig = {
  code: '',
}

const defaultDropStepConfig: DropStepConfig = {
  dropEntireEvent: true,
  dropFields: [''],
}

const defaultRegexStepConfig: RegexStepConfig = {
  field: '',
  regex: '',
  keepMatch: true,
}

const defaultSampleStepConfig: SampleStepConfig = {
  samplePercentage: 50,
}

export const DEFAULT_CONFIG: Record<NodeStepType, Config> = {
  [NodeStepType.Filter]: defaultFilterStepConfig,
  [NodeStepType.Code]: defaultCodeStepConfig,
  [NodeStepType.Drop]: defaultDropStepConfig,
  [NodeStepType.Regex]: defaultRegexStepConfig,
  [NodeStepType.Sample]: defaultSampleStepConfig,
}

function getDefaultConfig(stepType: NodeStepType): Config {
  return DEFAULT_CONFIG[stepType]
}

export type NodeStepState = {
  [key: string]: Config
}

interface NodeStepStore {
  stepId: string
  setStepId: (stepId: string) => void
  nodeStepType: NodeStepType
  setNodeStepType: (nodeStepType: NodeStepType) => void
  config: NodeStepState
  setConfig: (stepId: string, nodeStepType: NodeStepType, config: Partial<Config>) => void
  getConfig: (stepId: string, nodeStepType: NodeStepType) => Config
  getAllStepConfigs: () => NodeStepState
}

export const useNodeStepStore = create<NodeStepStore>((set, get) => ({
  stepId: '',
  setStepId: (stepId: string) => set({ stepId }),
  nodeStepType: NodeStepType.Filter,
  setNodeStepType: (nodeStepType: NodeStepType) => set({ nodeStepType }),
  config: {} as NodeStepState,
  setConfig: (stepId: string, nodeStepType: NodeStepType, config: Partial<Config>) =>
    set((state) => {
      const newConfig = {
        ...state.config,
        [stepId]: {
          ...getDefaultConfig(nodeStepType),
          ...state.config[stepId],
          ...config,
        },
      }

      return { config: newConfig }
    }),
  getConfig: (stepId: string, nodeStepType: NodeStepType) => {
    const state = get()
    return state.config[stepId] || getDefaultConfig(nodeStepType)
  },
  getAllStepConfigs: () => {
    const state = get()
    return state.config
  },
}))
